import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Calendar,
  Users,
  CalendarDays,
  X,
  Clock,
  Bike,
  HeartHandshake,
  Landmark,
  Pencil,
  Phone,
  CheckCircle2,
  Check,
  ChevronDown,
  Search,
  RotateCcw,
  LayoutGrid,
  List,
  UserCheck,
  Ban,
  MapPin,
  MoreVertical,
  Eye,
  UserPlus,
  type LucideIcon,
} from "lucide-react";
import { fmtDateOnly } from "@/lib/format-date";
import { getBooking, listBookings, patchBooking, type Booking } from "@/lib/bookings-api";
import { listBookingsForAdmin } from "@/lib/yatra-db";
import { listWalks } from "@/lib/walks-api";
import { listGuides } from "@/lib/guides-api";
import { listVolunteers } from "@/lib/volunteers-api";
import { listRickshaws } from "@/lib/rickshaws-api";
import { listBreakfast } from "@/lib/breakfast-api";

type B = Booking;

/** VisitBooking.TimeSlot — a choices field, not a clock time. Labels match get_time_slot_display. */
const TIME_SLOT_OPTIONS = [
  { value: "early_morning", label: "Early Morning (6 - 8 AM)" },
  { value: "morning", label: "Morning (8 - 11 AM)" },
  { value: "afternoon", label: "Afternoon (2 - 5 PM)" },
  { value: "evening", label: "Evening (5 - 7 PM)" },
];

/** Fallback for rows that arrive without the server's time_slot_display. */
function slotLabel(slot: string): string {
  return TIME_SLOT_OPTIONS.find((s) => s.value === slot)?.label || slot || "—";
}

/** VisitBooking.Status — the model only defines these two. */
const STATUSES = ["confirmed", "cancelled"];

/**
 * Per-type badge styling. Types are separated by fill weight (flat / tinted / solid) rather than
 * by border or hue alone — the palette has too few hues for that to read at a glance. The icon
 * carries the distinction too, so it never rests on colour alone.
 */
const TYPE_BADGE: Record<string, { cls: string; Icon: LucideIcon }> = {
  rickshaw: {
    cls: "border-border bg-muted text-muted-foreground",
    Icon: Bike,
  },
  volunteer: {
    cls: "border-primary/30 bg-primary/15 text-primary",
    Icon: HeartHandshake,
  },
  visit: {
    cls: "border-transparent bg-secondary-foreground text-secondary",
    Icon: Landmark,
  },
};

function TypeBadge({ type }: { type: string }) {
  const { cls, Icon } = TYPE_BADGE[type.toLowerCase()] ?? {
    cls: "border-dashed border-border bg-transparent text-muted-foreground",
    Icon: Calendar,
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${cls}`}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {type || "Not Assigned"}
    </span>
  );
}

/** Walk-name badges — each walk type gets a distinct colour. */
const WALK_NAME_BADGE: Record<string, string> = {
  "volunteer free walk": "border-emerald-200 bg-emerald-50 text-emerald-800",
  "walk with street food": "border-orange-200 bg-orange-50 text-orange-800",
  "guided walk": "border-blue-200 bg-blue-50 text-blue-800",
  "self walk": "border-violet-200 bg-violet-50 text-violet-800",
};

function WalkTypeBadge({ walkName, type }: { walkName: string; type: string }) {
  const label = walkName || type || "Walk";
  const key = label.toLowerCase();
  const cls =
    WALK_NAME_BADGE[key] ??
    (key.includes("volunteer")
      ? WALK_NAME_BADGE["volunteer free walk"]
      : key.includes("street food")
        ? WALK_NAME_BADGE["walk with street food"]
        : key.includes("guided")
          ? WALK_NAME_BADGE["guided walk"]
          : key.includes("self")
            ? WALK_NAME_BADGE["self walk"]
            : "border-border bg-muted/60 text-muted-foreground");
  return (
    <span
      className={`inline-flex max-w-[180px] items-center truncate rounded-lg border px-2.5 py-1 text-[11px] font-semibold ${cls}`}
    >
      {label}
    </span>
  );
}

function Avatar({ name, muted }: { name: string; muted?: boolean }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold ${
        muted
          ? "bg-muted text-muted-foreground"
          : "bg-primary/10 text-primary ring-1 ring-primary/15"
      }`}
    >
      {initial}
    </span>
  );
}

function slotRange(slot: string): string {
  const map: Record<string, string> = {
    early_morning: "6:00 AM – 8:00 AM",
    morning: "8:00 AM – 11:00 AM",
    afternoon: "2:00 PM – 5:00 PM",
    evening: "5:00 PM – 7:00 PM",
  };
  return map[slot] ?? "";
}

function slotShortLabel(slot: string, display?: string): string {
  if (display) {
    const m = display.match(/^([^(]+)/);
    return m?.[1]?.trim() || display;
  }
  const map: Record<string, string> = {
    early_morning: "Early Morning",
    morning: "Morning",
    afternoon: "Afternoon",
    evening: "Evening",
  };
  return map[slot] || slot || "Not Assigned";
}

const STATUS_PILL: Record<string, string> = {
  confirmed: "border-green-200 bg-green-50 text-green-700",
  cancelled: "border-red-200 bg-red-50 text-red-700",
  pending: "border-orange-200 bg-orange-50 text-orange-700",
  completed: "border-blue-200 bg-blue-50 text-blue-700",
};
const STATUS_PILL_FALLBACK = "border-border bg-muted/50 text-muted-foreground";

function noteEmoji(text: string): string {
  const t = text.toLowerCase();
  if (t.includes("birthday")) {
    return "🎂";
  }
  if (t.includes("anniversary")) {
    return "💍";
  }
  if (t.includes("yatra") || t.includes("jain")) {
    return "🙏";
  }
  return "📝";
}

export const Route = createFileRoute("/admin/bookings")({ component: Page });

function Page() {
  const [filter, setFilter] = useState<"all" | "today" | "upcoming" | "group" | "pending">("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "confirmed" | "cancelled">("all");
  const [guideFilter, setGuideFilter] = useState("all"); // "all" | "unassigned" | <guideId>
  const [providerFilter, setProviderFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedNoteView, setNoteView] = useState<B | null>(null);
  const noteDetail = useQuery({
    queryKey: ["jgy-booking-detail", selectedNoteView?.id],
    queryFn: () => getBooking(selectedNoteView!.id),
    enabled: selectedNoteView?.type === "visit",
  });
  const noteView = noteDetail.data ?? selectedNoteView;
  const [editing, setEditing] = useState<B | null>(null);
  // Table on desktop, cards on small screens — but a manual toggle overrides afterward.
  const [view, setView] = useState<"table" | "card">(() =>
    typeof window !== "undefined" && window.matchMedia?.("(max-width: 767px)").matches
      ? "card"
      : "table",
  );

  const resetFilters = () => {
    setFilter("all");
    setDateFrom("");
    setDateTo("");
    setTypeFilter("all");
    setStatusFilter("all");
    setGuideFilter("all");
    setProviderFilter("all");
    setSearch("");
  };

  const qc = useQueryClient();

  const {
    data: apiList = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["jgy-bookings"],
    queryFn: listBookings,
  });

  // Guide roster for the inline Guide column dropdown.
  const { data: guides = [] } = useQuery({ queryKey: ["jgy-guides"], queryFn: listGuides });
  // Volunteer roster — used to show the assigned volunteer's phone in the Provider column.
  const { data: volunteers = [] } = useQuery({
    queryKey: ["jgy-volunteers"],
    queryFn: listVolunteers,
  });

  // Partial PATCH so assigning a guide never re-submits the rest of the booking.
  const assignGuideMut = useMutation({
    mutationFn: ({ id, guideId }: { id: string; guideId: string }) =>
      patchBooking(id, { guide_id: guideId ? Number(guideId) || guideId : null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jgy-bookings"] });
      toast.success("Guide updated");
    },
    onError: (e: unknown) => {
      qc.invalidateQueries({ queryKey: ["jgy-bookings"] });
      toast.error(e instanceof Error ? e.message : "Could not assign guide");
    },
  });

  // Merge backend bookings with locally-submitted ones (e.g. Visit Form) so
  // devotee submissions surface here immediately. Backend is authoritative on id.
  const list = useMemo(() => {
    const local = listBookingsForAdmin();
    const seen = new Set(apiList.map((b) => b.id));
    const merged = [...apiList, ...local.filter((b) => !seen.has(b.id))];
    return merged.sort((a, b) => b.booking_date.localeCompare(a.booking_date));
  }, [apiList]);

  const today = new Date().toISOString().slice(0, 10);
  const stats = useMemo(() => {
    const todayList = list.filter((b) => b.booking_date === today);
    const upcoming = list.filter((b) => b.booking_date > today);
    const group = list.filter((b) => (b.group_size || 1) > 4);
    const pending = list.filter((b) => b.status === "pending");
    const confirmed = list.filter((b) => b.status === "confirmed");
    const cancelled = list.filter((b) => b.status === "cancelled");
    // A walk that includes a guide but none assigned yet.
    const pendingGuide = list.filter((b) => b.walk_is_guide !== false && !b.guide_id);
    return { todayList, upcoming, group, pending, confirmed, cancelled, pendingGuide };
  }, [list, today]);

  const types = useMemo(
    () => Array.from(new Set(list.map((b) => b.type).filter(Boolean))).sort(),
    [list],
  );

  const providers = useMemo(
    () => Array.from(new Set(list.map((b) => b.provider_name).filter(Boolean))).sort(),
    [list],
  );

  const filtered = useMemo(() => {
    let rows: B[];
    switch (filter) {
      case "today":
        rows = stats.todayList;
        break;
      case "upcoming":
        rows = stats.upcoming;
        break;
      case "group":
        rows = stats.group;
        break;
      case "pending":
        rows = stats.pending;
        break;
      default:
        rows = list;
    }
    if (dateFrom) {
      rows = rows.filter((b) => b.booking_date >= dateFrom);
    }
    if (dateTo) {
      rows = rows.filter((b) => b.booking_date <= dateTo);
    }
    if (typeFilter !== "all") {
      rows = rows.filter((b) => b.type === typeFilter);
    }
    if (statusFilter !== "all") {
      rows = rows.filter((b) => b.status === statusFilter);
    }
    if (guideFilter === "unassigned") {
      rows = rows.filter((b) => b.walk_is_guide !== false && !b.guide_id);
    } else if (guideFilter !== "all") {
      rows = rows.filter((b) => b.guide_id === guideFilter);
    }
    if (providerFilter !== "all") {
      rows = rows.filter((b) => b.provider_name === providerFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((b) =>
        [b.member_name, b.member_phone, b.walk_name, b.provider_name, b.guide_name, b.notes, b.type]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q),
      );
    }
    return rows;
  }, [
    filter,
    list,
    stats,
    dateFrom,
    dateTo,
    typeFilter,
    statusFilter,
    guideFilter,
    providerFilter,
    search,
  ]);

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => patchBooking(id, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jgy-bookings"] });
      toast.success("Status updated");
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Could not update status"),
  });

  const setStatus = (id: string, status: string) => statusMut.mutate({ id, status });

  // Shared cell renderers so Table and Card views stay in sync and reuse the same handlers.
  const devoteeCell = (b: B) => {
    const area = b.type === "visit" ? b.starting_point : b.meeting_point || b.meet_up;
    return (
      <div className="flex items-center gap-3">
        <Avatar name={b.member_name} />
        <div className="min-w-0">
          <div className="truncate font-semibold text-foreground">{b.member_name || "Devotee"}</div>
          {b.member_phone ? (
            <a
              href={`tel:${b.member_phone}`}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition hover:text-primary"
            >
              <Phone className="h-3 w-3" /> {b.member_phone}
            </a>
          ) : (
            <span className="text-[11px] text-muted-foreground/60">No phone</span>
          )}
          {area ? (
            <div className="mt-0.5 flex items-center gap-1 truncate text-[10px] text-muted-foreground/80">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate" title={area}>
                {b.type === "visit" ? "Starting point: " : ""}
                {area}
              </span>
            </div>
          ) : null}
          {b.type === "visit" && b.meeting_point && (
            <div className="mt-1 text-[10px] text-primary" title={b.meeting_point}>
              Admin override: {b.meeting_point}
            </div>
          )}
        </div>
      </div>
    );
  };

  const providerCell = (b: B) => {
    const vPhone = volunteers.find((v) => v.id === b.volunteer_id)?.phone || b.volunteer_phone;
    if (!b.provider_name) {
      return <span className="text-xs text-muted-foreground/60">Not Assigned</span>;
    }
    return (
      <div className="flex items-center gap-2.5">
        <Avatar name={b.provider_name} muted />
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-foreground">{b.provider_name}</div>
          {b.volunteer_id && vPhone ? (
            <a
              href={`tel:${vPhone}`}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary"
            >
              <Phone className="h-3 w-3" /> {vPhone}
            </a>
          ) : null}
        </div>
      </div>
    );
  };

  const guidesForBooking = (b: B) => {
    const pref = b.preferred_guide_gender?.toLowerCase();
    if (!pref || pref === "any") {
      return guides;
    }
    return guides.filter((g) => !g.gender || g.gender.toLowerCase() === pref);
  };

  const guideCell = (b: B) => {
    if (b.walk_is_guide === false) {
      return <span className="text-xs text-muted-foreground/60">Not included</span>;
    }
    const eligible = guidesForBooking(b);
    const roster =
      b.guide_id && !eligible.some((g) => g.id === b.guide_id)
        ? [{ id: b.guide_id, full_name: b.guide_name || "Assigned" }, ...eligible]
        : eligible;
    const gPhone = guides.find((g) => g.id === b.guide_id)?.phone || b.guide_phone;
    const pref = b.preferred_guide_gender?.toLowerCase();
    return (
      <div className="space-y-1">
        <PickerSelect
          value={b.guide_id}
          disabled={assignGuideMut.isPending || b.status === "cancelled"}
          placeholder="Assign Guide"
          options={roster.map((g) => ({ value: g.id, label: g.full_name }))}
          onChange={(guideId) => assignGuideMut.mutate({ id: b.id, guideId })}
        />
        {pref && pref !== "any" ? (
          <div className="text-[10px] text-muted-foreground">Prefers {pref} guide</div>
        ) : null}
        {b.guide_id && gPhone ? (
          <a
            href={`tel:${gPhone}`}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-primary"
          >
            <Phone className="h-3 w-3" /> {gPhone}
          </a>
        ) : null}
      </div>
    );
  };

  const dateTimeCell = (b: B) => (
    <div className="whitespace-nowrap">
      <div className="flex items-center gap-1.5 font-semibold text-foreground">
        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
        {fmtDateOnly(b.booking_date)}
      </div>
      <div className="mt-0.5 flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
        <Clock className="h-3 w-3" />
        {slotShortLabel(b.booking_time, b.time_slot_display)}
      </div>
      {slotRange(b.booking_time) ? (
        <div className="ml-5 text-[10px] text-muted-foreground/70">{slotRange(b.booking_time)}</div>
      ) : null}
    </div>
  );

  const walkTypeCell = (b: B) => (
    <div className="flex flex-col gap-1.5">
      <WalkTypeBadge walkName={b.walk_name} type={b.type} />
      {b.type && b.walk_name ? <TypeBadge type={b.type} /> : null}
    </div>
  );

  const groupTag = (b: B) => {
    const n = b.group_size || 1;
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/60 px-2.5 py-1 text-[11px] font-semibold text-foreground">
        <Users className="h-3.5 w-3.5 text-muted-foreground" />
        {n} {n === 1 ? "Member" : "Members"}
      </span>
    );
  };

  const notesCell = (b: B) =>
    b.notes ? (
      <button
        type="button"
        onClick={() => setNoteView(b)}
        className="inline-flex max-w-[200px] items-center gap-1.5 truncate rounded-xl border border-border bg-muted/40 px-2.5 py-1.5 text-[11px] font-medium text-foreground shadow-sm transition hover:border-primary/30 hover:bg-muted/70"
      >
        <span>{noteEmoji(b.notes)}</span>
        <span className="truncate">{b.notes}</span>
      </button>
    ) : (
      <span className="text-xs text-muted-foreground/60">No notes</span>
    );

  const actionCell = (b: B) => (
    <ActionMenu
      booking={b}
      onView={() => setNoteView(b)}
      onEdit={() => setEditing(b)}
      onAssignGuide={() => setEditing(b)}
      onCancel={() => {
        if (b.status === "cancelled") {
          return;
        }
        if (window.confirm(`Cancel booking for ${b.member_name || "this devotee"}?`)) {
          setStatus(b.id, "cancelled");
        }
      }}
    />
  );

  const statusPill = (b: B) => (
    <select
      value={b.status}
      onChange={(e) => setStatus(b.id, e.target.value)}
      className={`cursor-pointer rounded-full border px-3 py-1 text-[11px] font-semibold capitalize outline-none transition ${STATUS_PILL[b.status] ?? STATUS_PILL_FALLBACK}`}
    >
      {STATUSES.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  );

  const statActive = {
    total: filter === "all" && statusFilter === "all" && guideFilter === "all",
    confirmed: statusFilter === "confirmed",
    cancelled: statusFilter === "cancelled",
    pendingGuide: guideFilter === "unassigned",
    today: filter === "today",
  };

  const filterSelect =
    "h-9 rounded-xl border border-border bg-card px-3 text-xs font-semibold text-muted-foreground shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-bold tracking-tight">Visit Bookings</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage walk bookings, guides, and devotee schedules
          </p>
        </div>
        <div className="inline-flex rounded-xl border border-border bg-card p-1 shadow-sm">
          {(
            [
              { key: "table", label: "Table", Icon: List },
              { key: "card", label: "Cards", Icon: LayoutGrid },
            ] as const
          ).map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => setView(v.key)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold transition ${
                view === v.key
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              }`}
            >
              <v.Icon className="h-3.5 w-3.5" />
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <DashStat
          label="Total Bookings"
          value={list.length}
          icon={CalendarDays}
          tone="slate"
          active={statActive.total}
          onClick={() => {
            setFilter("all");
            setStatusFilter("all");
            setGuideFilter("all");
          }}
        />
        <DashStat
          label="Confirmed"
          value={stats.confirmed.length}
          icon={CheckCircle2}
          tone="green"
          active={statActive.confirmed}
          onClick={() => {
            setFilter("all");
            setStatusFilter(statusFilter === "confirmed" ? "all" : "confirmed");
          }}
        />
        <DashStat
          label="Cancelled"
          value={stats.cancelled.length}
          icon={Ban}
          tone="red"
          active={statActive.cancelled}
          onClick={() => {
            setFilter("all");
            setStatusFilter(statusFilter === "cancelled" ? "all" : "cancelled");
          }}
        />
        <DashStat
          label="Pending Guide"
          value={stats.pendingGuide.length}
          icon={UserCheck}
          tone="amber"
          active={statActive.pendingGuide}
          onClick={() => {
            setFilter("all");
            setGuideFilter(guideFilter === "unassigned" ? "all" : "unassigned");
          }}
        />
        <DashStat
          label="Today's Walks"
          value={stats.todayList.length}
          icon={Calendar}
          tone="blue"
          active={statActive.today}
          onClick={() => setFilter(filter === "today" ? "all" : "today")}
        />
      </div>

      {/* Filters toolbar */}
      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search devotee, walk, guide, provider…"
              className="h-9 w-full rounded-xl border border-border bg-background pl-9 pr-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
            />
          </div>
          <input
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
            className={filterSelect}
            title="From date"
          />
          <input
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
            className={filterSelect}
            title="To date"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            className={filterSelect}
          >
            <option value="all">All statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className={filterSelect}
          >
            <option value="all">All walk types</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <select
            value={guideFilter}
            onChange={(e) => setGuideFilter(e.target.value)}
            className={filterSelect}
          >
            <option value="all">All guides</option>
            <option value="unassigned">Unassigned</option>
            {guides.map((g) => (
              <option key={g.id} value={g.id}>
                {g.full_name}
              </option>
            ))}
          </select>
          <select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className={filterSelect}
          >
            <option value="all">All providers</option>
            {providers.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-border bg-background px-3 text-xs font-semibold text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{filtered.length}</span> of{" "}
          {list.length} bookings
        </p>
      </div>

      {/* Table view */}
      {view === "table" ? (
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
          <div className="max-h-[calc(100vh-22rem)] overflow-auto">
            <table className="w-full min-w-[1100px] border-separate border-spacing-0 text-sm">
              <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur-sm">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <th className="border-b border-border px-4 py-3.5">Devotee</th>
                  <th className="border-b border-border px-4 py-3.5">Walk Type</th>
                  <th className="border-b border-border px-4 py-3.5">Provider</th>
                  <th className="border-b border-border px-4 py-3.5">Guide</th>
                  <th className="border-b border-border px-4 py-3.5">Date & Time</th>
                  <th className="border-b border-border px-4 py-3.5">Starting Point</th>
                  <th className="border-b border-border px-4 py-3.5">Group</th>
                  <th className="border-b border-border px-4 py-3.5">Notes</th>
                  <th className="border-b border-border px-4 py-3.5">Status</th>
                  <th className="border-b border-border px-4 py-3.5">Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-12 text-center text-sm text-muted-foreground"
                    >
                      Loading bookings…
                    </td>
                  </tr>
                )}
                {isError && (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center text-sm text-destructive">
                      {error instanceof Error ? error.message : "Failed to load bookings"}
                    </td>
                  </tr>
                )}
                {!isLoading && !isError && filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-12 text-center text-sm text-muted-foreground"
                    >
                      No bookings match your filters
                    </td>
                  </tr>
                )}
                {filtered.map((b) => (
                  <tr
                    key={b.id}
                    className="group transition hover:bg-muted/30 [&>td]:border-b [&>td]:border-border/60"
                  >
                    <td className="px-4 py-4">{devoteeCell(b)}</td>
                    <td className="px-4 py-4">{walkTypeCell(b)}</td>
                    <td className="px-4 py-4">{providerCell(b)}</td>
                    <td className="px-4 py-4 min-w-[200px]">{guideCell(b)}</td>
                    <td className="px-4 py-4">{dateTimeCell(b)}</td>
                    <td className="min-w-[160px] px-4 py-4">
                      {b.type === "visit" && b.starting_point ? (
                        <div className="flex items-start gap-2 text-xs font-medium text-foreground">
                          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                          <span>{b.starting_point}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {b.type === "visit" ? "Not provided" : "—"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">{groupTag(b)}</td>
                    <td className="px-4 py-4">{notesCell(b)}</td>
                    <td className="px-4 py-4">{statusPill(b)}</td>
                    <td className="px-4 py-4">{actionCell(b)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Card view */
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {isLoading && (
            <div className="col-span-full py-12 text-center text-sm text-muted-foreground">
              Loading bookings…
            </div>
          )}
          {isError && (
            <div className="col-span-full py-12 text-center text-sm text-destructive">
              {error instanceof Error ? error.message : "Failed to load bookings"}
            </div>
          )}
          {!isLoading && !isError && filtered.length === 0 && (
            <div className="col-span-full py-12 text-center text-sm text-muted-foreground">
              No bookings match your filters
            </div>
          )}
          {filtered.map((b) => (
            <article
              key={b.id}
              className="flex flex-col rounded-2xl border border-border bg-card p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                {devoteeCell(b)}
                {statusPill(b)}
              </div>
              <div className="mt-4 space-y-3 border-t border-border/60 pt-4 text-sm">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Walk Type
                  </div>
                  <div className="mt-1">{walkTypeCell(b)}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Provider
                    </div>
                    <div className="mt-1">{providerCell(b)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Guide
                    </div>
                    <div className="mt-1">{guideCell(b)}</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  {dateTimeCell(b)}
                  {groupTag(b)}
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Notes
                  </div>
                  <div className="mt-1">{notesCell(b)}</div>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-end gap-2 border-t border-border/60 pt-4">
                <button
                  type="button"
                  onClick={() => setEditing(b)}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-background px-3 py-2 text-xs font-semibold text-muted-foreground transition hover:border-primary/40 hover:text-primary"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>
                {actionCell(b)}
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && <BookingEditor booking={editing} onClose={() => setEditing(null)} />}

      {noteView && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center jgy-overlay-scrim p-4 backdrop-blur-sm"
          onClick={() => setNoteView(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-card p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-border pb-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Booking Details
                </div>
                <div className="font-serif text-lg font-bold">
                  {noteView.member_name || "Devotee"}
                </div>
                {noteView.member_phone && (
                  <a
                    href={`tel:${noteView.member_phone}`}
                    className="inline-flex items-center gap-1 text-xs text-primary"
                  >
                    <Phone className="h-3 w-3" /> {noteView.member_phone}
                  </a>
                )}
              </div>
              <button type="button" onClick={() => setNoteView(null)} aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-3 space-y-3 text-sm">
              {selectedNoteView?.type === "visit" && noteDetail.isFetching && (
                <p className="text-xs text-muted-foreground">Loading booking details...</p>
              )}
              {noteDetail.isError && (
                <p className="text-xs text-destructive" role="alert">
                  {noteDetail.error.message}{" "}
                  <button className="underline" onClick={() => void noteDetail.refetch()}>
                    Retry
                  </button>
                </p>
              )}
              {noteView.type === "visit" && (
                <div className="rounded-xl border border-border bg-muted/30 p-3">
                  <div className="text-[10px] font-semibold uppercase text-muted-foreground">
                    Starting point
                  </div>
                  <div className="mt-1 flex items-center gap-2 font-medium">
                    <MapPin className="h-4 w-4 text-primary" />
                    {noteView.starting_point || "Not provided"}
                  </div>
                  {noteView.meeting_point && (
                    <p className="mt-2 text-xs text-primary">
                      Admin meeting override: {noteView.meeting_point}
                    </p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-xl border border-border bg-muted/30 p-2.5">
                  <div className="text-[10px] font-semibold uppercase text-muted-foreground">
                    Walk
                  </div>
                  <div className="mt-0.5 font-medium">
                    {noteView.walk_name || noteView.type || "Not Assigned"}
                  </div>
                </div>
                <div className="rounded-xl border border-border bg-muted/30 p-2.5">
                  <div className="text-[10px] font-semibold uppercase text-muted-foreground">
                    Status
                  </div>
                  <div className="mt-0.5 font-medium capitalize">{noteView.status}</div>
                </div>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Date & Time</span>
                <span>
                  {fmtDateOnly(noteView.booking_date)} ·{" "}
                  {noteView.time_slot_display || slotLabel(noteView.booking_time)}
                </span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Guide</span>
                <span>{noteView.guide_name || "Not Assigned"}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Provider</span>
                <span>{noteView.provider_name || "Not Assigned"}</span>
              </div>
              {noteView.visitor_names && (
                <div className="rounded-xl border border-border bg-muted/40 p-3">
                  <div className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">
                    Visitors
                  </div>
                  <pre className="whitespace-pre-wrap text-xs">{noteView.visitor_names}</pre>
                </div>
              )}
              <div className="rounded-xl border border-border bg-muted/40 p-3">
                <div className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">
                  Remarks
                </div>
                <p className="text-xs">{noteView.notes || "No notes"}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Themed single-select. A native <select> can't style its option list (the OS draws it), so this
 * renders its own panel. The panel is `fixed` and positioned from the trigger's rect because the
 * table scroller (`overflow-x-auto`) would otherwise clip an absolutely-positioned child.
 */
function ActionMenu({
  booking,
  onView,
  onEdit,
  onAssignGuide,
  onCancel,
}: {
  booking: B;
  onView: () => void;
  onEdit: () => void;
  onAssignGuide: () => void;
  onCancel: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-border bg-background text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
        aria-label="Actions"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open ? (
        <div className="absolute right-0 top-full z-20 mt-1 min-w-[168px] rounded-xl border border-border bg-card p-1 shadow-lg">
          <MenuBtn
            icon={Eye}
            label="View"
            onClick={() => {
              onView();
              setOpen(false);
            }}
          />
          <MenuBtn
            icon={Pencil}
            label="Edit"
            onClick={() => {
              onEdit();
              setOpen(false);
            }}
          />
          {booking.walk_is_guide !== false ? (
            <MenuBtn
              icon={UserPlus}
              label="Assign Guide"
              onClick={() => {
                onAssignGuide();
                setOpen(false);
              }}
            />
          ) : null}
          {booking.status !== "cancelled" ? (
            <MenuBtn
              icon={Ban}
              label="Cancel Booking"
              danger
              onClick={() => {
                onCancel();
                setOpen(false);
              }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function MenuBtn({
  icon: Icon,
  label,
  onClick,
  danger,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-medium transition hover:bg-muted ${
        danger ? "text-destructive" : "text-foreground"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

function DashStat({
  label,
  value,
  icon: Icon,
  tone,
  active,
  onClick,
}: {
  label: string;
  value: number;
  icon: LucideIcon;
  tone: "slate" | "green" | "red" | "amber" | "blue";
  active: boolean;
  onClick: () => void;
}) {
  const tones = {
    slate: "border-border bg-card text-foreground",
    green: "border-green-200/80 bg-green-50/80 text-green-900",
    red: "border-red-200/80 bg-red-50/80 text-red-900",
    amber: "border-amber-200/80 bg-amber-50/80 text-amber-900",
    blue: "border-blue-200/80 bg-blue-50/80 text-blue-900",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl border p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${tones[tone]} ${
        active ? "ring-2 ring-primary/25" : ""
      }`}
    >
      <Icon className="h-4 w-4 opacity-70" />
      <div className="mt-2 text-2xl font-bold tabular-nums">{value}</div>
      <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider opacity-75">
        {label}
      </div>
    </button>
  );
}

function PickerSelect({
  value,
  options,
  placeholder = "—",
  disabled,
  onChange,
}: {
  value: string;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !panelRef.current?.contains(t)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    // Any scroll would detach the fixed panel from its trigger, so just close it.
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  const toggle = () => {
    if (disabled) {
      return;
    }
    if (open) {
      setOpen(false);
      return;
    }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const width = Math.max(r.width, 220);
      const estimated = Math.min(options.length + 1, 6) * 34 + 12;
      // Flip above when there isn't room below.
      const below = window.innerHeight - r.bottom;
      setPos({
        top: below < estimated ? r.top - estimated - 6 : r.bottom + 6,
        left: Math.min(r.left, window.innerWidth - width - 12),
        width,
      });
    }
    setOpen(true);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`inline-flex w-full min-w-[200px] max-w-[240px] items-center justify-between gap-1.5 rounded-xl border px-3 py-2 text-xs font-medium transition-colors disabled:opacity-60 ${
          open
            ? "border-primary bg-card text-foreground"
            : selected
              ? "border-green-300 bg-green-50 text-green-800 hover:border-green-400"
              : "border-amber-300 bg-amber-50 text-amber-700 hover:border-amber-400"
        }`}
      >
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? "rotate-180" : ""} ${
            selected ? "text-green-700" : "text-amber-600"
          }`}
        />
      </button>

      {open && pos ? (
        <div
          ref={panelRef}
          role="listbox"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
          className="fixed z-[60] max-h-56 overflow-y-auto rounded-xl border border-border bg-card p-1 shadow-[var(--shadow-warm)]"
        >
          <Opt
            label={placeholder}
            active={!value}
            muted
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
          />
          {options.map((o) => (
            <Opt
              key={o.value}
              label={o.label}
              active={o.value === value}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

function Opt({
  label,
  active,
  muted,
  onClick,
}: {
  label: string;
  active: boolean;
  muted?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onClick={onClick}
      className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs transition-colors ${
        active
          ? "bg-primary/10 font-semibold text-primary"
          : `hover:bg-muted ${muted ? "text-muted-foreground" : "text-foreground"}`
      }`}
    >
      <span className="truncate">{label}</span>
      {active ? <Check className="h-3.5 w-3.5 shrink-0" /> : null}
    </button>
  );
}

const TIME_SLOTS = TIME_SLOT_OPTIONS;

const PAYMENT_STATUSES = ["pending", "paid", "partial", "refunded"];
const PAYMENT_METHODS = ["cash", "upi", "card", "bank_transfer"];
/** The API stores the no-preference case as the literal "any", not an empty string. */
const GENDER_PREFS = [
  { value: "any", label: "Any" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];

type BookingDraft = {
  status: string;
  visit_date: string;
  time_slot: string;
  meeting_point: string;
  visitor_count: number;
  adults: number;
  children: number;
  seniors: number;
  member_names: string;
  preferred_volunteer_gender: string;
  preferred_guide_gender: string;
  guide_id: string;
  volunteer_id: string;
  rickshaw_id: string;
  breakfast_id: string;
  final_amount: number;
  payment_status: string;
  payment_method: string;
  notes: string;
};

function toBookingDraft(b: B): BookingDraft {
  return {
    status: b.status,
    visit_date: b.booking_date,
    // No slicing — this is a slug like "afternoon", not a HH:MM time.
    time_slot: b.booking_time || "",
    meeting_point: b.meeting_point,
    visitor_count: b.group_size ?? 1,
    adults: b.adults,
    children: b.children,
    seniors: b.seniors,
    member_names: b.visitor_names ?? "",
    preferred_volunteer_gender: b.preferred_volunteer_gender,
    preferred_guide_gender: b.preferred_guide_gender,
    guide_id: b.guide_id,
    volunteer_id: b.volunteer_id,
    rickshaw_id: b.rickshaw_id,
    breakfast_id: b.breakfast_id,
    final_amount: b.final_amount,
    payment_status: b.payment_status,
    payment_method: b.payment_method,
    notes: b.notes ?? "",
  };
}

/**
 * Build a PATCH body containing only the fields the admin actually changed.
 * Sending every field at once lets one server-managed/read-only value (empty payment_status,
 * an assignment the walk doesn't allow, …) reject or silently drop the whole update — the same
 * failure the inline single-field patches avoid. A diff keeps each edit isolated.
 */
function buildBookingChanges(b: B, d: BookingDraft): Record<string, unknown> {
  const rel = (v: string) => (v ? (/^\d+$/.test(v) ? Number(v) : v) : null);
  const lines = (s: string) =>
    s
      .split("\n")
      .map((x) => x.trim())
      .filter(Boolean);
  const c: Record<string, unknown> = {};
  if (d.status !== b.status) {
    c.status = d.status;
  }
  if (d.visit_date !== b.booking_date) {
    c.visit_date = d.visit_date;
  }
  if (d.time_slot !== (b.booking_time || "")) {
    c.time_slot = d.time_slot;
  }
  if (d.meeting_point !== b.meeting_point) {
    c.meeting_point = d.meeting_point;
  }
  if (d.visitor_count !== (b.group_size ?? 1)) {
    c.visitor_count = d.visitor_count;
  }
  if (d.adults !== b.adults) {
    c.adults = d.adults;
  }
  if (d.children !== b.children) {
    c.children = d.children;
  }
  if (d.seniors !== b.seniors) {
    c.seniors = d.seniors;
  }
  if (lines(d.member_names).join("\n") !== lines(b.visitor_names ?? "").join("\n")) {
    c.member_names = lines(d.member_names);
  }
  if (d.preferred_volunteer_gender !== b.preferred_volunteer_gender) {
    c.preferred_volunteer_gender = d.preferred_volunteer_gender;
  }
  if (d.preferred_guide_gender !== b.preferred_guide_gender) {
    c.preferred_guide_gender = d.preferred_guide_gender;
  }
  if (d.guide_id !== b.guide_id) {
    c.guide_id = rel(d.guide_id);
  }
  if (d.volunteer_id !== b.volunteer_id) {
    c.volunteer_id = rel(d.volunteer_id);
  }
  if (d.rickshaw_id !== b.rickshaw_id) {
    c.rickshaw_id = rel(d.rickshaw_id);
  }
  if (d.breakfast_id !== b.breakfast_id) {
    c.breakfast_id = rel(d.breakfast_id);
  }
  if (d.final_amount !== b.final_amount) {
    c.final_amount = d.final_amount;
  }
  if (d.payment_status !== b.payment_status) {
    c.payment_status = d.payment_status;
  }
  if (d.payment_method !== b.payment_method) {
    c.payment_method = d.payment_method;
  }
  if ((d.notes || "") !== (b.notes ?? "")) {
    c.notes = d.notes;
  }
  return c;
}

function BookingEditor({ booking, onClose }: { booking: B; onClose: () => void }) {
  const detail = useQuery({
    queryKey: ["jgy-booking-detail", booking.id],
    queryFn: () => getBooking(booking.id),
    enabled: booking.type === "visit",
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  if (booking.type !== "visit") {
    return <BookingEditorForm booking={booking} onClose={onClose} />;
  }
  if (detail.isPending || detail.isError || detail.isFetching) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center jgy-overlay-scrim p-4 backdrop-blur-sm">
        <div className="w-full max-w-md space-y-4 rounded-2xl bg-card p-6">
          <h3 className="font-serif text-xl font-bold">Edit Booking</h3>
          {detail.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {detail.error.message}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">Loading booking details...</p>
          )}
          <div className="flex gap-3">
            <button className="btn-outline py-2 text-sm" onClick={onClose}>
              Close
            </button>
            {detail.isError && (
              <button className="btn-primary py-2 text-sm" onClick={() => void detail.refetch()}>
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
  return <BookingEditorForm booking={detail.data} onClose={onClose} />;
}

function BookingEditorForm({ booking, onClose }: { booking: B; onClose: () => void }) {
  const qc = useQueryClient();
  const [d, setD] = useState<BookingDraft>(() => toBookingDraft(booking));
  const u = (patch: Partial<BookingDraft>) => setD((prev) => ({ ...prev, ...patch }));

  // Assignment dropdowns are sourced from the real admin APIs.
  const { data: walks = [] } = useQuery({ queryKey: ["jgy-walks"], queryFn: listWalks });
  const { data: guides = [] } = useQuery({ queryKey: ["jgy-guides"], queryFn: listGuides });
  const { data: volunteers = [] } = useQuery({
    queryKey: ["jgy-volunteers"],
    queryFn: listVolunteers,
  });
  const { data: rickshaws = [] } = useQuery({
    queryKey: ["jgy-rickshaws"],
    queryFn: listRickshaws,
  });
  const { data: breakfasts = [] } = useQuery({
    queryKey: ["jgy-breakfast"],
    queryFn: listBreakfast,
  });

  const saveMut = useMutation({
    mutationFn: () => {
      const changes = buildBookingChanges(booking, d);
      // No-op when nothing changed — avoids an empty PATCH.
      if (Object.keys(changes).length === 0) {
        return Promise.resolve(booking);
      }
      return patchBooking(booking.id, changes);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jgy-bookings"] });
      qc.invalidateQueries({ queryKey: ["jgy-booking-detail", booking.id] });
      toast.success("Booking updated");
      onClose();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Something went wrong"),
  });

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto jgy-overlay-scrim p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mx-auto my-8 w-full max-w-2xl rounded-2xl bg-card p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3 border-b border-border pb-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Edit Booking
            </div>
            <h3 className="font-serif text-xl font-bold">{booking.member_name || "Devotee"}</h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
              {booking.member_phone ? (
                <a
                  href={`tel:${booking.member_phone}`}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  <Phone className="h-3 w-3" /> {booking.member_phone}
                </a>
              ) : null}
              {booking.member_email ? (
                <a
                  href={`mailto:${booking.member_email}`}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  {booking.member_email}
                </a>
              ) : null}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <Fieldset title="Walk & Schedule">
            {/* The walk is fixed at booking time (it's tied to the payment) and the serializer
                drops it on update, so it's shown read-only rather than as a live select. */}
            <BookingField label="Walk Option">
              <div className="binp bg-muted/50 text-muted-foreground">
                {walks.find((w) => w.id === booking.walk_id)?.title || booking.walk_name || "—"}
              </div>
            </BookingField>
            <BookingField label="Walk Price">
              <div className="binp bg-muted/50 text-muted-foreground">
                {booking.walk_is_free
                  ? "Free"
                  : booking.walk_price
                    ? `₹${booking.walk_price.toLocaleString("en-IN")}`
                    : "—"}
              </div>
            </BookingField>
            <BookingField label="Status">
              <select
                className="binp"
                value={d.status}
                onChange={(e) => u({ status: e.target.value })}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </BookingField>
            <BookingField label="Walk Date">
              <input
                type="date"
                className="binp"
                value={d.visit_date}
                onChange={(e) => u({ visit_date: e.target.value })}
              />
            </BookingField>
            <BookingField label="Time Slot">
              <select
                className="binp"
                value={d.time_slot}
                onChange={(e) => u({ time_slot: e.target.value })}
              >
                <option value="">—</option>
                {/* Keep an unrecognised existing slot selectable so editing never rewrites it. */}
                {(TIME_SLOTS.some((s) => s.value === d.time_slot) || !d.time_slot
                  ? TIME_SLOTS
                  : [{ value: d.time_slot, label: d.time_slot }, ...TIME_SLOTS]
                ).map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </BookingField>
            {booking.type === "visit" && (
              <BookingField label="Starting Point" full>
                <div className="binp flex items-center gap-2 bg-muted/50 text-muted-foreground">
                  <MapPin className="h-4 w-4 shrink-0 text-primary" />
                  {booking.starting_point || "Not provided"}
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Selected at booking time, or the walk default. Read-only.
                </p>
              </BookingField>
            )}
            <BookingField label="Admin Meeting Point Override (optional)" full>
              <input
                className="binp"
                value={d.meeting_point}
                onChange={(e) => u({ meeting_point: e.target.value })}
                placeholder="—"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Set a custom meeting location for this booking only.
              </p>
            </BookingField>
          </Fieldset>

          <Fieldset title="Group Details">
            <BookingField label="Group Size">
              <input
                type="number"
                min={1}
                className="binp"
                value={d.visitor_count}
                onChange={(e) => u({ visitor_count: e.target.value ? +e.target.value : 0 })}
              />
            </BookingField>
            <BookingField label="Adults">
              <input
                type="number"
                min={0}
                className="binp"
                value={d.adults}
                onChange={(e) => u({ adults: e.target.value ? +e.target.value : 0 })}
              />
            </BookingField>
            <BookingField label="Children">
              <input
                type="number"
                min={0}
                className="binp"
                value={d.children}
                onChange={(e) => u({ children: e.target.value ? +e.target.value : 0 })}
              />
            </BookingField>
            <BookingField label="Seniors">
              <input
                type="number"
                min={0}
                className="binp"
                value={d.seniors}
                onChange={(e) => u({ seniors: e.target.value ? +e.target.value : 0 })}
              />
            </BookingField>
            <BookingField label="Visitor Names (one per line)" full>
              <textarea
                rows={3}
                className="binp"
                value={d.member_names}
                onChange={(e) => u({ member_names: e.target.value })}
              />
            </BookingField>
            <BookingField label="Preferred Volunteer Gender">
              <select
                className="binp"
                value={d.preferred_volunteer_gender}
                onChange={(e) => u({ preferred_volunteer_gender: e.target.value })}
              >
                {GENDER_PREFS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>
            </BookingField>
            {/* The server rejects an assigned guide whose gender contradicts this. */}
            <BookingField label="Preferred Guide Gender">
              <select
                className="binp"
                value={d.preferred_guide_gender}
                onChange={(e) => u({ preferred_guide_gender: e.target.value })}
              >
                {GENDER_PREFS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>
            </BookingField>
          </Fieldset>

          <Fieldset title="Assignment">
            {/* The serializer rejects assignments the walk doesn't offer, so those pickers are
                replaced by a note rather than letting the admin pick and get a 400. */}
            <BookingField label="Assign Guide">
              {booking.walk_is_guide === false ? (
                <div className="binp bg-muted/50 text-muted-foreground">
                  Not included in this walk
                </div>
              ) : (
                <select
                  className="binp"
                  value={d.guide_id}
                  onChange={(e) => u({ guide_id: e.target.value })}
                >
                  <option value="">— None —</option>
                  {guides.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.full_name}
                    </option>
                  ))}
                </select>
              )}
            </BookingField>
            <BookingField label="Assign Volunteer">
              {booking.walk_is_volunteer === false ? (
                <div className="binp bg-muted/50 text-muted-foreground">
                  Not included in this walk
                </div>
              ) : (
                <select
                  className="binp"
                  value={d.volunteer_id}
                  onChange={(e) => u({ volunteer_id: e.target.value })}
                >
                  <option value="">— None —</option>
                  {volunteers.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              )}
            </BookingField>
            {booking.volunteer_id && booking.volunteer_name ? (
              <BookingField label="Volunteer Details" full>
                <div className="binp bg-muted/50 text-muted-foreground">
                  <div className="font-medium text-foreground">
                    {booking.volunteer_name}
                    {booking.volunteer_gender ? ` · ${booking.volunteer_gender}` : ""}
                    {booking.volunteer_status ? (
                      <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold capitalize text-green-700">
                        {booking.volunteer_status}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-0.5 text-xs">
                    {[booking.volunteer_phone, booking.volunteer_email]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </div>
                  {booking.volunteer_accepted_at ? (
                    <div className="mt-0.5 text-[10px]">
                      Accepted {fmtDateTime(booking.volunteer_accepted_at)}
                    </div>
                  ) : null}
                </div>
              </BookingField>
            ) : null}
            <BookingField label="Assign Rickshaw">
              <select
                className="binp"
                value={d.rickshaw_id}
                onChange={(e) => u({ rickshaw_id: e.target.value })}
              >
                <option value="">— None —</option>
                {rickshaws.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.driver_name}
                  </option>
                ))}
              </select>
            </BookingField>
            <BookingField label="Breakfast Option">
              {booking.walk_is_breakfast === false ? (
                <div className="binp bg-muted/50 text-muted-foreground">
                  Not included in this walk
                </div>
              ) : (
                <select
                  className="binp"
                  value={d.breakfast_id}
                  onChange={(e) => u({ breakfast_id: e.target.value })}
                >
                  <option value="">— None —</option>
                  {breakfasts.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              )}
            </BookingField>
          </Fieldset>

          <Fieldset title="Payment">
            {/* amount_paid is set from the Razorpay payment at booking time — read-only. */}
            <BookingField label="Amount Paid (₹)">
              <div className="binp bg-muted/50 text-muted-foreground">
                ₹{booking.amount_paid.toLocaleString("en-IN")}
              </div>
            </BookingField>
            <BookingField label="Final Amount (₹)">
              <input
                type="number"
                min={0}
                className="binp"
                value={d.final_amount}
                onChange={(e) => u({ final_amount: e.target.value ? +e.target.value : 0 })}
              />
            </BookingField>
            <BookingField label="Payment Status">
              <select
                className="binp"
                value={d.payment_status}
                onChange={(e) => u({ payment_status: e.target.value })}
              >
                <option value="">—</option>
                {PAYMENT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </BookingField>
            <BookingField label="Payment Method">
              <select
                className="binp"
                value={d.payment_method}
                onChange={(e) => u({ payment_method: e.target.value })}
              >
                <option value="">—</option>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m.replace("_", " ")}
                  </option>
                ))}
              </select>
            </BookingField>
          </Fieldset>

          <Fieldset title="Cancellation & Refund">
            <BookingField label="Free Cancellation">
              <div className="binp bg-muted/50 text-muted-foreground">
                {booking.is_free_cancellation === null
                  ? "—"
                  : booking.is_free_cancellation
                    ? "Yes"
                    : "No"}
              </div>
            </BookingField>
            <BookingField label="Charge If Cancelled Now (₹)">
              <div className="binp bg-muted/50 text-muted-foreground">
                ₹{booking.cancellation_charge_now.toLocaleString("en-IN")}
              </div>
            </BookingField>
            <BookingField label="Cancellation Charge (₹)">
              <div className="binp bg-muted/50 text-muted-foreground">
                ₹{booking.cancellation_charge.toLocaleString("en-IN")}
              </div>
            </BookingField>
            <BookingField label="Refund Amount (₹)">
              <div className="binp bg-muted/50 text-muted-foreground">
                ₹{booking.refund_amount.toLocaleString("en-IN")}
              </div>
            </BookingField>
            {booking.cancelled_at ? (
              <BookingField label="Cancelled At" full>
                <div className="binp bg-muted/50 text-muted-foreground">
                  {fmtDateTime(booking.cancelled_at)}
                </div>
              </BookingField>
            ) : null}
          </Fieldset>

          <Fieldset title="Notes">
            <BookingField label="Customer Remarks" full>
              <textarea
                rows={2}
                className="binp"
                value={d.notes}
                onChange={(e) => u({ notes: e.target.value })}
              />
            </BookingField>
          </Fieldset>

          <div className="flex flex-wrap gap-x-4 gap-y-1 px-1 text-[10px] text-muted-foreground">
            <span>Created {fmtDateTime(booking.created_at)}</span>
            {booking.updated_at ? <span>Updated {fmtDateTime(booking.updated_at)}</span> : null}
            <span>Booking ID {booking.id}</span>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2 border-t border-border pt-4">
          <button
            onClick={onClose}
            disabled={saveMut.isPending}
            className="btn-outline py-2 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
            className="btn-primary py-2 text-sm disabled:pointer-events-none disabled:opacity-60"
          >
            <CheckCircle2 className="h-4 w-4" />
            {saveMut.isPending ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
      <style>{`.binp{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--card));border-radius:.75rem;padding:.5rem .75rem;font-size:.875rem;color:hsl(var(--foreground));outline:none}.binp:focus{border-color:hsl(var(--primary))}`}</style>
    </div>
  );
}

/** Human date+time from an ISO string; em dash when missing/unparseable. */
function fmtDateTime(input: string | null | undefined): string {
  if (!input) {
    return "—";
  }
  const d = new Date(input);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN");
}

function Fieldset({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-muted/30 p-4">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function BookingField({
  label,
  full,
  children,
}: {
  label: string;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
